import React, { useEffect, useState } from "react";
import { AccountLayout } from "./AccountLayout";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { ShieldCheck, FileCheck2, Clock, AlertOctagon, Upload, FileText, Trash2 } from "lucide-react";

/**
 * Source-of-Funds + crypto-risk declaration page.
 * - Triggered for customers aged 65+ (sof_status='required' at signup; bands "65-70" and "70-80").
 * - Also triggered automatically when a single order > €5,000 is attempted.
 */
export default function SofPage() {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    sof_text: "", income_source: "", crypto_risk_ack: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/auth/sof");
      setStatus(r.data);
    } catch (e) {
      toast.error("Failed to load SoF status");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.crypto_risk_ack) { toast.error("Please acknowledge the crypto-risk declaration"); return; }
    if (form.sof_text.trim().length < 20) { toast.error("Please describe your source of funds (≥20 chars)"); return; }
    if (form.income_source.trim().length < 2) { toast.error("Please name your primary income source"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/sof/upload", form);
      toast.success("Submitted for review. We'll notify you when it's approved.");
      await refreshMe();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  const uploadDoc = async (file, label) => {
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) { toast.error("File too large — 12 MB max"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (label) fd.append("label", label);
      await api.post("/auth/sof/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded — securely stored.");
      await load();
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const removeDoc = async (id) => {
    if (!window.confirm("Remove this document?")) return;
    try {
      await api.delete(`/auth/sof/documents/${id}`);
      toast.success("Document removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (loading) return <AccountLayout title="Source of Funds"><div className="text-sm text-muted-foreground">Loading…</div></AccountLayout>;

  if (status?.status === "not_required") {
    return (
      <AccountLayout title="Source of Funds">
        <div className="xch-card p-12 text-center" data-testid="sof-not-required">
          <ShieldCheck className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
          <div className="font-display text-xl font-bold">Not required for your account</div>
          <p className="text-sm text-muted-foreground mt-1.5">
            Source-of-Funds declarations are only required for customers in specific age bands.
          </p>
          <Button onClick={() => navigate("/account")} className="mt-4 rounded-xl bg-primary hover:bg-primary/90 text-white">
            Back to account
          </Button>
        </div>
      </AccountLayout>
    );
  }

  return (
    <AccountLayout
      title="Source of Funds"
      subtitle="Required for customers 65+ or any order above €5,000. Once approved, no need to re-submit unless your circumstances change."
    >
      <div className="space-y-4">
        <div className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-xs sm:text-sm text-foreground/90 flex items-start gap-2 leading-relaxed"
             data-testid="sof-why-link-banner">
          <ShieldCheck className="w-4 h-4 mt-0.5 text-primary shrink-0" />
          <div>
            Wondering why we ask? Read <Link to="/legal/why-sof" className="text-primary font-semibold underline" data-testid="sof-why-link">why we ask for Source of Funds</Link> — the short version: layered EU regulation requires it on a risk-based basis. Your documents are encrypted at rest.
          </div>
        </div>

        <div className="xch-card p-4 flex items-start gap-3" data-testid="sof-status-card">
          <StatusIcon status={status?.status} />
          <div className="flex-1">
            <div className="font-display font-bold text-foreground flex items-center gap-2">
              {STATUS_LABEL[status?.status] || "Action required"}
              <Badge variant="outline" className={`text-[10px] uppercase font-bold tracking-wider ${STATUS_CLS[status?.status]}`}>
                {status?.status || "required"}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {STATUS_BODY[status?.status]}
            </p>
            {status?.latest?.submitted_at && (
              <div className="text-[10px] text-muted-foreground mt-1.5 tabular-nums">
                Submitted {new Date(status.latest.submitted_at).toLocaleString("en-IE")}
                {status.reviewed_at && ` · Reviewed ${new Date(status.reviewed_at).toLocaleString("en-IE")}`}
              </div>
            )}
            {status?.review_reason && (
              <div className="mt-2 text-xs italic">"{status.review_reason}"</div>
            )}
          </div>
        </div>

        {(status?.status === "required" || status?.status === "rejected") && (
          <form onSubmit={submit} className="xch-card p-5 space-y-4" data-testid="sof-form">
            <div>
              <div className="font-display text-base font-bold">Tell us where the money is coming from</div>
              <p className="text-xs text-muted-foreground mt-0.5">
                A short, factual description is enough — e.g. "Retirement pension paid monthly by Aviva" or "Sale proceeds from my apartment in 2024".
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                Primary income source
              </Label>
              <Input
                value={form.income_source}
                onChange={(e) => setForm({ ...form, income_source: e.target.value })}
                placeholder="e.g. Pension, savings, sale of property…"
                className="rounded-xl" data-testid="sof-income-source-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                Source-of-Funds declaration (min 20 characters)
              </Label>
              <Textarea
                value={form.sof_text}
                onChange={(e) => setForm({ ...form, sof_text: e.target.value })}
                placeholder="Describe how you obtained the funds you plan to use for crypto purchases…"
                className="rounded-xl min-h-[120px]"
                data-testid="sof-text-input"
              />
              <div className="text-[10px] text-muted-foreground text-right tabular-nums">
                {form.sof_text.length}/4000
              </div>
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer rounded-xl bg-amber-50/60 border border-amber-200 p-3" data-testid="sof-risk-ack-label">
              <Checkbox
                checked={form.crypto_risk_ack}
                onCheckedChange={(v) => setForm({ ...form, crypto_risk_ack: !!v })}
                className="mt-0.5"
                data-testid="sof-risk-ack-checkbox"
              />
              <div className="text-xs text-amber-900 leading-relaxed">
                <strong>I acknowledge the risks of buying cryptocurrency.</strong> Crypto assets are highly volatile, unregulated by most jurisdictions, and may lose all of their value. I am buying with money I can afford to lose and understand transactions are irreversible once on-chain.
              </div>
            </label>
            <Button type="submit" disabled={submitting}
                    className="w-full bg-primary hover:bg-primary/90 text-white rounded-xl font-display font-semibold"
                    data-testid="sof-submit-btn">
              {submitting ? "Submitting…" : "Submit for review"}
            </Button>
          </form>
        )}

        {(status?.status === "required" || status?.status === "rejected" || status?.status === "pending") && (
          <div className="xch-card p-5" data-testid="sof-docs-card">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <div>
                <div className="font-display text-base font-bold">Supporting documents</div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Upload a recent bank statement, payslip, or salary letter — PDF or image, max 12 MB each.
                  Your files are <strong>encrypted at rest</strong> and only readable by our compliance team.
                </p>
              </div>
              <label className="inline-flex">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  data-testid="sof-doc-input"
                  onChange={(e) => uploadDoc(e.target.files?.[0], "")}
                  disabled={uploading}
                />
                <Button type="button" disabled={uploading}
                        onClick={() => fileInputRef.current?.click()}
                        className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5"
                        data-testid="sof-doc-upload-btn">
                  <Upload className="w-4 h-4" />
                  {uploading ? "Uploading…" : "Upload document"}
                </Button>
              </label>
            </div>
            {(status?.documents || []).length === 0 ? (
              <div className="text-xs text-muted-foreground mt-3 rounded-xl border border-dashed border-border p-4 text-center">
                No documents uploaded yet.
              </div>
            ) : (
              <div className="divide-y divide-border mt-3 border border-border rounded-xl overflow-hidden">
                {status.documents.map((d) => (
                  <div key={d.id} className="p-3 flex items-center gap-3" data-testid={`sof-doc-${d.id}`}>
                    <FileText className="w-5 h-5 text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-foreground truncate text-sm">
                        {d.filename}
                      </div>
                      <div className="text-[10px] text-muted-foreground font-mono">
                        {d.content_type} · {(d.size_plain / 1024).toFixed(1)} KB ·
                        {" "}{new Date(d.uploaded_at).toLocaleString("en-IE")}
                      </div>
                    </div>
                    {status.status !== "approved" && (
                      <Button type="button" onClick={() => removeDoc(d.id)} variant="ghost" size="sm"
                              className="rounded-md text-rose-700 hover:bg-rose-50 gap-1.5"
                              data-testid={`sof-doc-${d.id}-remove-btn`}>
                        <Trash2 className="w-3.5 h-3.5" /> Remove
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {status?.status === "pending" && (
          <div className="xch-card p-5 bg-amber-50/50 border-amber-200" data-testid="sof-pending-notice">
            <div className="text-sm font-semibold text-amber-900 flex items-center gap-2">
              <Clock className="w-4 h-4" /> Under review
            </div>
            <p className="text-xs text-amber-900/80 mt-1">
              Most reviews complete in a few business hours. We'll send you a notification as soon as it's done.
              You can browse and quote in the meantime — orders are blocked until approved.
            </p>
          </div>
        )}
      </div>
    </AccountLayout>
  );
}

const STATUS_LABEL = {
  required: "Source-of-Funds declaration required",
  pending:  "Under review",
  approved: "Approved",
  rejected: "Rejected — please re-submit",
};
const STATUS_BODY = {
  required: "We need a short declaration about where the money is coming from, plus at least one supporting document (bank statement, payslip, or salary letter). Triggered automatically for accounts aged 65+ or any single order above €5,000.",
  pending:  "Your submission is with our compliance team.",
  approved: "Your declaration has been approved. You can now place orders.",
  rejected: "Your declaration was not accepted. Please review the reason and re-submit.",
};
const STATUS_CLS = {
  required: "bg-amber-50 text-amber-700 border-amber-200",
  pending:  "bg-sky-50 text-sky-700 border-sky-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
};
const StatusIcon = ({ status }) => {
  if (status === "approved") return <ShieldCheck className="w-7 h-7 text-emerald-600 shrink-0" />;
  if (status === "pending")  return <Clock className="w-7 h-7 text-sky-600 shrink-0" />;
  if (status === "rejected") return <AlertOctagon className="w-7 h-7 text-rose-600 shrink-0" />;
  return <FileCheck2 className="w-7 h-7 text-amber-600 shrink-0" />;
};
